
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Download, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { WebsiteSelector } from './WebsiteSelector';
import { ImportResult } from './ImportResult';
import { ImportResult as ImportResultType, WebsiteOption } from './types';

interface ImportAdsProps {
  onImportComplete?: () => void;
}

export const ImportAds = ({ onImportComplete }: ImportAdsProps) => {
  const [isImporting, setIsImporting] = useState(false);
  const [selectedWebsite, setSelectedWebsite] = useState<WebsiteOption>('all');
  const [lastImport, setLastImport] = useState<ImportResultType | null>(null);
  const { toast } = useToast();

  const handleImport = async () => {
    setIsImporting(true);
    setLastImport(null);
    
    try {
      console.log(`Starting ad import process for: ${selectedWebsite}`);
      
      const { data, error } = await supabase.functions.invoke('scrape-ads', {
        body: { website: selectedWebsite }
      });

      if (error) {
        throw error;
      }

      console.log('Import result:', data);
      setLastImport(data);

      if (data.success) {
        toast({
          title: "Import réussi",
          description: data.message,
        });
        onImportComplete?.();
      } else {
        toast({
          title: "Import échoué",
          description: data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error importing ads:', error);
      const errorResult = {
        success: false,
        message: 'Erreur lors de l\'importation des annonces',
        error: error.message
      };
      setLastImport(errorResult);
      
      toast({
        title: "Erreur d'importation",
        description: "Impossible d'importer les annonces. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-white">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2 text-blue-800">
          <Download className="h-5 w-5" />
          <span>Importation d'annonces</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-gray-700 text-sm">
          Sélectionnez le site à importer et lancez l'importation automatique :
        </p>
        
        <div className="space-y-3">
          <WebsiteSelector 
            selectedWebsite={selectedWebsite}
            onWebsiteChange={setSelectedWebsite}
          />

          <Button 
            onClick={handleImport} 
            disabled={isImporting}
            className="w-full bg-blue-600 hover:bg-blue-700"
          >
            {isImporting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Importation en cours...
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Importer les annonces
              </>
            )}
          </Button>
        </div>

        {lastImport && <ImportResult result={lastImport} />}
      </CardContent>
    </Card>
  );
};
